import {readQuerySafe, writeFragmentsToCache, writeQuery} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import runModal from 'ui-library/runModal';

import {BenchlingAppGraphQL} from '../../graphql-fragments/benchling-apps/__generated/BenchlingAppGraphQL';
import {
  deprecatedCreateBenchlingApp,
  CreateBenchlingAppArgs,
  activateBenchlingApp,
  deprecatedCreateBenchlingAppFromManifest,
  CreateBenchlingAppFromManifestArgs,
  checkLifecycleChangeTask,
  getInferredTenantInfo,
  getWebhookMessageTypes,
  TenantInferredInfo,
  TenantInferredInfoError,
  deactivateBenchlingApp,
  UpdateBenchlingAppDefinitionArgs,
  UpdateBenchlingAppDefinitionVersionArgs,
  deprecatedUpdateBenchlingAppDefinitionVersion,
  deprecatedUpdateBenchlingAppDefinition,
  CreateBenchlingAppArgsLegacy,
  CreateBenchlingAppFromManifestArgsLegacy,
  createBenchlingAppFromManifestLegacy,
  createBenchlingAppLegacy,
  sendTestPayloadMessage,
  UpdateBenchlingAppDefinitionVersionFromManifestArgs,
  deprecatedUpdateBenchlingAppDefinitionVersionFromManifest,
} from './BenchlingAppDAO';
import {ListBenchlingAppInstallationsQuery} from './developer/__generated/ListBenchlingAppInstallationsQuery';
import {
  BENCHLING_APP_INSTALLATIONS_LISTING_QUERY,
  BENCHLING_APP_LISTING_QUERY_PAGE_SIZE,
} from './developer/BenchlingAppsPage';
import DeactivateAppWarningModal from './developer/DeactivateAppWarningModal';

export async function deprecatedUpdateDefinitionVersion(
  appId: string,
  version: string | null,
  data: UpdateBenchlingAppDefinitionVersionArgs
): Promise<BenchlingAppGraphQL> {
  const response = await deprecatedUpdateBenchlingAppDefinitionVersion(appId, version, data);
  writeFragmentsToCache(getFragmentWrapper('BenchlingApp'), [response.benchlingApp]);
  return response.benchlingApp;
}

export async function deprecatedUpdateDefinitionVersionFromManifest(
  appId: string,
  version: string | null,
  data: UpdateBenchlingAppDefinitionVersionFromManifestArgs
): Promise<BenchlingAppGraphQL> {
  const response = await deprecatedUpdateBenchlingAppDefinitionVersionFromManifest(appId, version, data);
  writeFragmentsToCache(getFragmentWrapper('BenchlingApp'), [response.benchlingApp]);
  return response.benchlingApp;
}

export async function deprecatedUpdateDefinition(
  appId: string,
  data: UpdateBenchlingAppDefinitionArgs
): Promise<BenchlingAppGraphQL> {
  const response = await deprecatedUpdateBenchlingAppDefinition(appId, data);
  writeFragmentsToCache(getFragmentWrapper('BenchlingApp'), [response.benchlingApp]);
  return response.benchlingApp;
}

export async function deprecatedCreate(data: CreateBenchlingAppArgs): Promise<BenchlingAppGraphQL> {
  const response = await deprecatedCreateBenchlingApp(data);
  writeFragmentsToCache(getFragmentWrapper('BenchlingApp'), [response.benchlingApp]);
  addCreatedBenchlingAppToApolloCache(response.benchlingApp);
  return response.benchlingApp;
}

// TODO (BNCH-92479): remove this function when we migrate all legacy apps
export async function createLegacy(data: CreateBenchlingAppArgsLegacy): Promise<BenchlingAppGraphQL> {
  const response = await createBenchlingAppLegacy(data);
  writeFragmentsToCache(getFragmentWrapper('BenchlingApp'), [response.benchlingApp]);
  addCreatedBenchlingAppToApolloCache(response.benchlingApp);
  return response.benchlingApp;
}

export async function deprecatedCreateAppFromManifest(
  data: CreateBenchlingAppFromManifestArgs
): Promise<BenchlingAppGraphQL> {
  const response = await deprecatedCreateBenchlingAppFromManifest(data);
  writeFragmentsToCache(getFragmentWrapper('BenchlingApp'), [response.benchlingApp]);
  addCreatedBenchlingAppToApolloCache(response.benchlingApp);
  return response.benchlingApp;
}

// TODO (BNCH-92479): remove this function when we migrate all legacy apps
export async function createAppFromManifestLegacy(
  data: CreateBenchlingAppFromManifestArgsLegacy
): Promise<BenchlingAppGraphQL> {
  const response = await createBenchlingAppFromManifestLegacy(data);
  writeFragmentsToCache(getFragmentWrapper('BenchlingApp'), [response.benchlingApp]);
  addCreatedBenchlingAppToApolloCache(response.benchlingApp);
  return response.benchlingApp;
}

export async function activateApp(benchlingAppId: string): Promise<string> {
  const activateResponse = await activateBenchlingApp(benchlingAppId);
  return activateResponse.status_url;
}

export async function deactivateApp(benchlingAppId: string): Promise<string> {
  const deactivateResponse = await deactivateBenchlingApp(benchlingAppId);
  return deactivateResponse.status_url;
}

export async function checkLifecycleChangeStatus(statusUrl: string): Promise<string> {
  const lifecycleTask = await checkLifecycleChangeTask(statusUrl);
  if (lifecycleTask.status === 'SUCCESS' && lifecycleTask.result) {
    return lifecycleTask.result.deliveryStatus;
  } else {
    return lifecycleTask.status;
  }
}

export async function confirmDeactivateApp(): Promise<boolean> {
  const shouldDeactivateApp = await runModal(DeactivateAppWarningModal, {});
  return Boolean(shouldDeactivateApp);
}

export async function getMessageTypes(): Promise<Array<string>> {
  const webhookMessageTypes = await getWebhookMessageTypes();
  return webhookMessageTypes.messageTypes;
}

export async function sendTestMessage(
  appDefinitionId: string,
  payload: Record<string, unknown>,
  webhookUrl: string
): Promise<string> {
  const testResult = await sendTestPayloadMessage(appDefinitionId, payload, webhookUrl);
  return testResult.messageId;
}

export function lookupInferredTenantInfo(
  tenantUrl: string
): Promise<TenantInferredInfo | TenantInferredInfoError> {
  return getInferredTenantInfo({tenant_url_or_id: tenantUrl});
}

function addCreatedBenchlingAppToApolloCache(benchlingApp: BenchlingAppGraphQL): void {
  const cachedBenchlingApps: ListBenchlingAppInstallationsQuery | null = readQuerySafe({
    query: BENCHLING_APP_INSTALLATIONS_LISTING_QUERY,
  });
  let newBenchlingAppList = [];
  if (!cachedBenchlingApps) {
    return;
  }
  newBenchlingAppList = [benchlingApp, ...cachedBenchlingApps.benchlingAppListing.benchlingApps];
  newBenchlingAppList = newBenchlingAppList.slice(0, BENCHLING_APP_LISTING_QUERY_PAGE_SIZE);
  writeQuery(
    {query: BENCHLING_APP_INSTALLATIONS_LISTING_QUERY},
    {
      benchlingAppListing: {
        benchlingApps: newBenchlingAppList,
        __typename: 'BenchlingAppListingNode',
      },
    }
  );
}
